import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface ToastState {
  icon: string;
  message: string;
  visible: boolean;
}

interface ToastContextValue {
  showToast: (icon: string, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>({ icon: '', message: '', visible: false });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((icon: string, message: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ icon, message, visible: true });
    timerRef.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2800);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={`toast${toast.visible ? ' show' : ''}`}>
        <span>{toast.icon}</span>
        <span>{toast.message}</span>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
